import React, { useContext, useEffect, useState } from "react";
import SectionHeader from "../components/section/SectionHeader";
import {
  HISTORICAL_ALARM_TABLE_HEADERS,
  VIEW_HISTORICAL_ALARMS_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  transformHistoricalAlarmsResponseRows,
  transformHistoricalAlarmsRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import TableActionButton from "../ui/TableActionButton";
import { getHistoricalAlarmByRef } from "../api";
import ViewAlarmRow from "../components/dataTable/ViewAlarmRow";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import { getPageTranslationText } from "../translations/util";
import { Lang, Operation, Page } from "../translations/types";

const translations = getPageTranslationText({
  lang: Lang.EN,
  page: Page.HISTORICAL_ALARM,
  op: Operation.READ,
});

const HistoricalAlarms = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  useAppTitle("Historical Alarms - IWF Config Admin Tool (ICAT) by AT&T");
  const { showSidePanel, handleToggleSidePanel } = useHideSidePanel();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [inventoryData, setInventoryData] = useState<any[]>([]);

  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    pageNum: number;
    pageSize: number;
  }>({
    pageSize: 20,
    pageNum: 1,
  });

  const fetchHistoricalAlarms = async () => {
    setLoading(true);
    try {
      const { pageNum, pageSize } = searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
      });
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/fault-mgmt/alarms/historical?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setInventoryData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoricalAlarms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <>
      <SectionHeader
        pageTitle={translations.header}
        pageDescription={translations.headerSubtext}
        sideComponent={null}
      />

      <>
        {showSidePanel && selectedConnectionId ? (
          <ViewAlarmRow
            panelName={translations.editRow.panelName}
            org={selectedConnectionId}
            esRef={selectedConnectionId}
            setShowEditPanel={handleToggleSidePanel}
            formFields={VIEW_HISTORICAL_ALARMS_FORM_FIELDS}
            transformResponseRow={transformHistoricalAlarmsResponseRows}
            loadDataByRef={getHistoricalAlarmByRef}
          />
        ) : null}

        <DataTableWithControls
          loading={loading}
          showTableControls={false}
          filterColumns={[]}
          selectedColumn={null}
          setSelectedColumn={() => null}
          onSearch={() => fetchHistoricalAlarms()}
          showCreateButton={false}
          rows={transformHistoricalAlarmsRows(inventoryData).map(
            (row: { id: string }) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    handleToggleSidePanel();
                  }}
                />
              ),
            })
          )}
          headers={HISTORICAL_ALARM_TABLE_HEADERS}
          pageNum={searchQuery.pageNum}
          setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
          totalItems={numberOfRows}
          totalPages={numberOfPages}
          pageSize={searchQuery.pageSize}
          setPageSize={(pageSize) =>
            setSearhQuery({ ...searchQuery, pageSize })
          }
        />
      </>
    </>
  );
};

export default HistoricalAlarms;
